import { useInsertionEffect, useRef } from 'react';
import { shallow } from 'zustand/shallow';

/**
 * Compare-by-value identity for a derived record.
 *
 * Returns the PREVIOUSLY committed value whenever the new one is shallow-equal
 * (same keys, `Object.is` values — zustand's `shallow`, which also covers
 * arrays, `Map` and `Set`). A `useMemo` that rebuilds, say, a
 * `Record<ReportTypeId, Lifecycle>` from fresh inputs produces a NEW object
 * with the SAME contents on every progress tick; every memo and effect keyed
 * on it then recomputes for nothing. Routing the memo's result through this
 * hook turns "equal contents" into "same reference".
 *
 * The compare runs against the last COMMITTED value (updated in an insertion
 * effect), so a discarded render never poisons the baseline.
 */
export function useShallowStableRecord<T>(value: T): T {
  const committed = useRef(value);
  const stable = shallow(committed.current, value) ? committed.current : value;
  useInsertionEffect(() => {
    committed.current = stable;
  });
  return stable;
}
